import { TableData } from "examples/Tables/DataTable";
import {
  Duplicate,
  MergeRecordsMutationVariables,
  useMatcherTableDataQuery,
  useMergeRecordsMutation,
} from "../../../../graphql/types/graphql";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { PaginationProperties, usePagination } from "../../../../hooks/usePagination";
import { debounce, DebouncedFunc } from "lodash";
import { enqueueSnackbar } from "notistack";
import { PhaseData, useMatcher } from "utils/MatcherProvider";
import { GenericObject } from "components/PaginatedTable/GenericTable";
import { context } from "utils/Provider";

interface Props {
  matcherTableName: string;
}

interface DuplicatesService extends Omit<PaginationProperties, "modifyDataCount"> {
  matcherTableData: GenericObject[] | undefined;
  loading: {
    service: boolean;
    merge: boolean;
  };
  error: string | undefined;
  handleMergeRecords: (args: MergeArgs) => Promise<boolean>;
  selectedDuplicate: Duplicate | undefined;
  onSelectDuplicate: (duplicate: Duplicate) => void;
  onSelectMatch: (data: { cubeId: string; value: string; rowId: string }) => void;
  mergeData: { rowId: string; cubeId: string; value: string } | undefined;
  onCompanySearch: (text: string) => void;
  companyName: string | null;
}

type MergeArgs = Omit<MergeRecordsMutationVariables, "score">;

export function useDuplicates({ matcherTableName }: Props): DuplicatesService {
  const { selectedCube } = useContext(context);
  const { setPhaseData, setPhaseHistory } = useMatcher();
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [companyName, setCompanyName] = useState<string | null>(null);

  const { data, loading, error } = useMatcherTableDataQuery({
    variables: {
      tableName: matcherTableName,
      cubeId: selectedCube.cubeId,
      limit: pageSize,
      offset: pageSize * currentPage,
      filter: companyName
        ? {
            companyName,
          }
        : null,
    },
  });
  const [merge, { loading: loadingMerge }] = useMergeRecordsMutation({
    update: (cache, { data }) => {
      if (data.mergeRecords?.success) {
        cache.evict({ id: "ROOT_QUERY", fieldName: "queryMatchedViewsPhaseData" });
        cache.gc();
      }
    },
  });
  const [selectedDuplicate, setSelectedDuplicate] = useState<Duplicate | undefined>();
  const [mergeData, setMergeData] = useState<{ cubeId: string; value: string; rowId: string } | undefined>();

  const handleMergeRecords = useCallback(
    async ({ cubeId, fxId, identifier, tableName, value }: MergeArgs): Promise<boolean> => {
      try {
        const response = await merge({
          variables: {
            cubeId,
            fxId,
            identifier,
            score: 1,
            tableName,
            value,
          },
        });
        if (response) {
          setPhaseData((current) => {
            const result: PhaseData = {
              ...current,
              matchedCount: current.matchedCount + 1,
              duplicateCount: current.duplicateCount - 1,
              duplicates: current?.duplicates.filter((x) => x.FX_UNIQUE_ID !== selectedDuplicate.FX_UNIQUE_ID) ?? [],
            };
            setPhaseHistory((x) =>
              x.map((historyPhase) => {
                if (historyPhase.phase === result.phase) {
                  return result;
                }
                return historyPhase;
              })
            );
            return result;
          });

          setSelectedDuplicate(undefined);
          enqueueSnackbar("Rows have been merged successfully!", { variant: "success" });
          return Promise.resolve(true);
        }
        return Promise.resolve(false);
      } catch (error) {
        enqueueSnackbar("Couldn't resolve the manual matching", { variant: "error" });
        return Promise.resolve(false);
      }
    },
    [merge, selectedDuplicate?.FX_UNIQUE_ID, setPhaseData, setPhaseHistory]
  );

  const onSelectDuplicate = useCallback((duplicate: Duplicate) => setSelectedDuplicate(duplicate), []);

  const debounceRef = useRef<DebouncedFunc<(criteria: string) => void> | undefined>();

  const searchByCompanyName = useCallback((value: string) => {
    debounceRef.current?.cancel();
    debounceRef.current = debounce((criteria) => {
      if (criteria.length === 0) {
        setCompanyName(null);
      } else {
        setCompanyName(criteria);
      }
    }, 800);
    debounceRef.current(value);
  }, []);

  const matcherTableData: GenericObject[] | undefined = (() => {
    if (!data?.matcherTableData) {
      return undefined;
    }
    return data.matcherTableData.edges.map((current, index) => ({
      id: `${index}.${currentPage}.${companyName}`,
      value: {
        ...current,
      },
    }));
  })();

  useEffect(() => {
    setMergeData(undefined);
  }, [pageSize, companyName]);

  return {
    handleMergeRecords,
    matcherTableData,
    loading: {
      merge: loadingMerge,
      service: loading,
    },
    error: error?.message,
    selectedDuplicate,
    onSelectDuplicate,
    changePage: setCurrentPage,
    changePageSize: setPageSize,
    pageData: {
      page: currentPage,
      pageSize,
      count: data?.matcherTableData.totalCount ?? 0,
    },
    mergeData,
    onSelectMatch: setMergeData,
    onCompanySearch: searchByCompanyName,
    companyName,
  };
}
